/** Ремап значення з діапазону [v_min, v_max] в [out_min, out_max] з опціональним затисканням */
export function implementRemap(): string
{
  return `
    float remap(float v, float v_min, float v_max, float out_min, float out_max)
    {
      float denom = v_max - v_min;
      denom = abs(denom) < 1e-6 ? (denom < 0.0 ? -1e-6 : 1e-6) : denom;
      return mix(out_min, out_max, clamp((v - v_min) / denom, 0.0, 1.0));
    }

    float remap(float v, float v_min, float v_max, float out_min, float out_max, bool do_clamp)
    {
      float denom = v_max - v_min;
      denom = abs(denom) < 1e-6 ? (denom < 0.0 ? -1e-6 : 1e-6) : denom;
      float t = (v - v_min) / denom;
      return mix(out_min, out_max, do_clamp ? clamp(t, 0.0, 1.0) : t);
    }
    `;
}

/** Ремап значення з діапазону [v_min, v_max] в [out_min, out_max] з опціональним затисканням */
export function implementRemapVector2(): string
{
  return `
    vec2 remapVector2(float v, float v_min, float v_max, vec2 out_min, vec2 out_max)
    {
      float denom = v_max - v_min;
      denom = abs(denom) < 1e-6 ? (denom < 0.0 ? -1e-6 : 1e-6) : denom;
      return mix(out_min, out_max, clamp((v - v_min) / denom, 0.0, 1.0));
    }

    vec2 remapVector2(float v, float v_min, float v_max, vec2 out_min, vec2 out_max, bool do_clamp)
    {
      float denom = v_max - v_min;
      denom = abs(denom) < 1e-6 ? (denom < 0.0 ? -1e-6 : 1e-6) : denom;
      float t = (v - v_min) / denom;
      return mix(out_min, out_max, do_clamp ? clamp(t, 0.0, 1.0) : t);
    }
    `;
}

/** Ремап значення з діапазону [v_min, v_max] в [out_min, out_max] з опціональним затисканням */
export function implementRemapVector3(): string
{
  return `
    vec3 remapVector3(float v, float v_min, float v_max, vec3 out_min, vec3 out_max)
    {
      float denom = v_max - v_min;
      denom = abs(denom) < 1e-6 ? (denom < 0.0 ? -1e-6 : 1e-6) : denom;
      return mix(out_min, out_max, clamp((v - v_min) / denom, 0.0, 1.0));
    }

    vec3 remapVector3(float v, float v_min, float v_max, vec3 out_min, vec3 out_max, bool do_clamp)
    {
      float denom = v_max - v_min;
      denom = abs(denom) < 1e-6 ? (denom < 0.0 ? -1e-6 : 1e-6) : denom;
      float t = (v - v_min) / denom;
      return mix(out_min, out_max, do_clamp ? clamp(t, 0.0, 1.0) : t);
    }
    `;
}

/** Ремап значення з діапазону [v_min, v_max] в [out_min, out_max] з опціональним затисканням */
export function implementRemapVector4(): string
{
  return `
    vec4 remapVector4(float v, float v_min, float v_max, vec4 out_min, vec4 out_max)
    {
      float denom = v_max - v_min;
      denom = abs(denom) < 1e-6 ? (denom < 0.0 ? -1e-6 : 1e-6) : denom;
      return mix(out_min, out_max, clamp((v - v_min) / denom, 0.0, 1.0));
    }

    vec4 remapVector4(float v, float v_min, float v_max, vec4 out_min, vec4 out_max, bool do_clamp)
    {
      float denom = v_max - v_min;
      denom = abs(denom) < 1e-6 ? (denom < 0.0 ? -1e-6 : 1e-6) : denom;
      float t = (v - v_min) / denom;
      return mix(out_min, out_max, do_clamp ? clamp(t, 0.0, 1.0) : t);
    }
    `;
}

/** 
 * Обчислює clip-space координаты
 */
export function implementCalculateClipSpace(): string
{
  return `
    vec4 calculateClipSpace(vec2 position, vec2 resolution)
    {
      vec2 clip = (position / resolution) * 2.0 - 1.0;
      return vec4(clip * vec2(1.0, -1.0), 0.0, 1.0);
    }
    `;
}

/** Функция для расчёта вершины прямоугольника */
export function implementGetVertexFromIndex(): string
{
  return `
    vec2 getVertexFromIndex(int idx, vec2 center, float angle, vec2 size)
    {
      float c = cos(angle);
      float s = sin(angle);

      // Локальные координаты углов (до поворота)
      vec2 local;
      if      (idx == 0)               local = vec2(-size.x * 0.5, -size.y * 0.5); // TL (Top is -Y in Y-down)
      else if (idx == 1 || idx == 4)   local = vec2( size.x * 0.5, -size.y * 0.5); // TR
      else if (idx == 2 || idx == 3)   local = vec2(-size.x * 0.5,  size.y * 0.5); // BL (Bottom is +Y in Y-down)
      else                             local = vec2( size.x * 0.5,  size.y * 0.5); // BR

      // Поворот
      vec2 rotated = vec2(
        local.x * c - local.y * s,
        local.x * s + local.y * c
      );

      return center + rotated;
    }
    `;
}

/** Повертає "кут" (0 або 1 по кожній осі) для 6 вершин двох трикутників */
export function implementGetCornerFromIndex(): string
{
  return `
    vec2 getCornerFromIndex(int idx)
    {
      // Стандарт: y=0 это ВЕРХ, y=1 это НИЗ (как в gl_PointCoord и Canvas)
      return (idx == 0) ? vec2(0.0, 0.0) :  // TL
             (idx == 1) ? vec2(1.0, 0.0) :  // TR
             (idx == 2) ? vec2(0.0, 1.0) :  // BL
             (idx == 3) ? vec2(0.0, 1.0) :  // BL
             (idx == 4) ? vec2(1.0, 0.0) :  // TR
                          vec2(1.0, 1.0);   // BR
    }
    `;
}

/** Обчислює UV-координати спрайту в атласі за індексом */
export function implementGetSpriteUV(): string
{
  return `
    void getSpriteUV(
      int index,
      vec2 atlas_size,
      ivec2 grid,
      vec2 padding,
      out vec2 min_uv,
      out vec2 max_uv)
    {
      int cols = grid.x;
      int rows = grid.y;
      int count = cols * rows;

      int idx = clamp(index, 0, count - 1);

      int col = idx % cols;
      int row = idx / cols;

      vec2 cell = atlas_size / vec2(float(cols), float(rows));

      // row 0 (верхний в атласе) должен иметь V близкое к 1.0
      float row_top = float(rows - 1 - row);

      // min_uv.y - это ВЕРХ спрайта (соответствует uv.y = 0)
      // max_uv.y - это НИЗ спрайта (соответствует uv.y = 1)
      vec2 px_min = vec2(float(col), row_top) * cell + padding;
      vec2 px_max = px_min + cell - 2.0 * padding;

      // При FLIP_Y=true: V_max (верх) должен соответствовать uv.y=0
      min_uv = vec2(px_min.x, px_max.y) / atlas_size;
      max_uv = vec2(px_max.x, px_min.y) / atlas_size;
    }
    `;
}

/** Обчислює нормалізовану відстань від центру (0.5, 0.5) до країв (0..1) */
export function implementRadialDist(): string
{
  return `
    float radialDist(vec2 coord)
    {
      return clamp(length(coord - 0.5) * 2.0, 0.0, 1.0);
    }
    `;
}

/** 
 * Обчислює скалярне значення за нормалізованим часом життя (0..1)
 * Працює з масивом ключів (values + keys). Лінійна інтерполяція між сусідніми ключами.
 *
 * Вимагає remap() з webgl_common_funcs.ts
 */
export function implementEvalFloat( values_array_name: string, key_count_name: string, keys_array_name: string ): string
{
  return `
    float evalFloat(float t)
    {
      if (t <= ${keys_array_name}[0]) return ${values_array_name}[0];
      if (t >= ${keys_array_name}[${key_count_name} - 1]) return ${values_array_name}[${key_count_name} - 1];

      int i = 1;
      for (; i < ${key_count_name} - 1; i++)
        if (t < ${keys_array_name}[i])
          break;

      float t0 = ${keys_array_name}[i - 1];
      float t1 = ${keys_array_name}[i];

      return remap(t, t0, t1, ${values_array_name}[i - 1], ${values_array_name}[i], true);
    }
    `;
}

/** 
 * Обчислює vec3-значення за нормалізованим часом життя (0..1)
 * Працює з масивом ключів (values + keys). Лінійна інтерполяція між сусідніми ключами.
 *
 * Вимагає remapVector3() з webgl_common_funcs.ts
 */
export function implementEvalVec3( values_array_name: string, key_count_name: string, keys_array_name: string ): string
{
  return `
    vec3 evalVec3(float t)
    {
      if (t <= ${keys_array_name}[0]) return ${values_array_name}[0];
      if (t >= ${keys_array_name}[${key_count_name} - 1]) return ${values_array_name}[${key_count_name} - 1];

      int i = 1;
      for (; i < ${key_count_name} - 1; i++)
        if (t < ${keys_array_name}[i])
          break;

      float t0 = ${keys_array_name}[i - 1];
      float t1 = ${keys_array_name}[i];

      return remapVector3(t, t0, t1, ${values_array_name}[i - 1], ${values_array_name}[i], true);
    }
    `;
}

/**
 * Обчислює точку на кубічній кривій Безьє
 */
export function implementCubicBezier(): string
{
  return `
    vec2 cubicBezier(vec2 p0, vec2 p1, vec2 p2, vec2 p3, float t)
    {
      float mt = 1.0 - t;
      float mt2 = mt * mt;
      float mt3 = mt2 * mt;
      float t2 = t * t;
      float t3 = t2 * t;

      return mt3 * p0 + 3.0 * mt2 * t * p1 + 3.0 * mt * t2 * p2 + t3 * p3;
    }
    `;
}